import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsService } from '@/api/services/projects';
import type { ProjectCreate, ProjectResponse, ProjectUpdate } from '@/api/types';
import { fitLauncherRootKey } from './useFitLauncher';

export const projectsQueryKey = ['projects'] as const;
export const projectQueryKey = (id: string) => ['projects', id] as const;

export function useProjects() {
  return useQuery({ queryKey: projectsQueryKey, queryFn: projectsService.list });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectQueryKey(id ?? ''),
    queryFn: () => projectsService.detail(id as string),
    enabled: !!id,
  });
}

/** Creates a plain project and puts it in the cached list straight away, so a
 *  picker that offers only the user's own projects can select it at once. */
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectCreate) => projectsService.create(body),
    onSuccess: (project) => {
      qc.setQueryData<ProjectResponse[]>(projectsQueryKey, (old) =>
        old ? [project, ...old.filter((p) => p.id !== project.id)] : [project],
      );
      return qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectUpdate) => projectsService.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectsQueryKey });
      qc.invalidateQueries({ queryKey: projectQueryKey(id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsService.remove(id),
    // A project's launcher deployments go with it; their keys live under
    // their own root, so they are invalidated explicitly.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: projectsQueryKey }),
        qc.invalidateQueries({ queryKey: fitLauncherRootKey }),
      ]),
  });
}

export function useRefreshRecommendations(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => projectsService.refreshRecommendations(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectQueryKey(id) });
    },
  });
}
